import React, { useState, useEffect } from "react";
import { useHistory } from "react-router-dom";
import {
  Typography,
  Table,
  TableContainer,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  Paper,
  Button,
  TextField,
  Grid,
  Box,
  Chip,
  Stack,
  Divider,
  useMediaQuery,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { API } from "../../../config";
import axios from "axios";
import CustomerLayout from "../CustomerLayout";
import OrderTimeline from "../../../components/OrderTimeline";

const OrderHistory = () => {
  const history = useHistory();
  const theme = useTheme();

  // Tablets and phones should use cards, not a compressed table.
 const isSmallScreen = useMediaQuery("(max-width:1199px)");
  const isTouchDevice = useMediaQuery("(hover: none), (pointer: coarse)");
  const isMobileOrTablet = isSmallScreen || isTouchDevice;

  const [orders, setOrders] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [filteredOrders, setFilteredOrders] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [ordersPerPage] = useState(5);

  const [selectedOrderId, setSelectedOrderId] = useState(null);
  const [timeline, setTimeline] = useState([]);
  const [timelineLoading, setTimelineLoading] = useState(false);
  const [timelineError, setTimelineError] = useState(null);

  const [aiText, setAiText] = useState("");
  const [aiLoading, setAiLoading] = useState(false);
  const [aiError, setAiError] = useState(null);

  const [unreadNegotiations, setUnreadNegotiations] = useState({});

  useEffect(() => {
    fetchOrderHistory();
  }, []);

  useEffect(() => {
  const unreadKey = "hagglefreight_customer_unread_negotiations";

  const loadUnread = () => {
    try {
      const saved = JSON.parse(
        localStorage.getItem(unreadKey) || "{}"
      );

      setUnreadNegotiations(saved);
    } catch (error) {
      console.error("Failed to load unread negotiations:", error);
      setUnreadNegotiations({});
    }
  };

  loadUnread();

  window.addEventListener(
    "hagglefreight-customer-unread-changed",
    loadUnread
  );

  return () => {
    window.removeEventListener(
      "hagglefreight-customer-unread-changed",
      loadUnread
    );
  };
}, []);

  const fetchOrderHistory = async () => {
    try {
      const token =
        localStorage.getItem("customer_token") ||
        localStorage.getItem("token");

      const response = await axios.get(`${API}/api/order-history`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      const data = Array.isArray(response.data) ? response.data : [];

      setOrders(data);
      setFilteredOrders(data);
    } catch (error) {
      console.error("Error fetching order history:", error);
      setOrders([]);
      setFilteredOrders([]);
    }
  };

  const fetchTimeline = async (orderId) => {
    try {
      setTimelineLoading(true);
      setTimelineError(null);

      const token =
        localStorage.getItem("customer_token") ||
        localStorage.getItem("token");

      const res = await fetch(`${API}/api/orders/${orderId}/timeline`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (!res.ok) {
        throw new Error(`Failed to load timeline (${res.status})`);
      }

      const data = await res.json();
      setTimeline(data.events || []);
    } catch (err) {
      setTimeline([]);
      setTimelineError(err.message);
    } finally {
      setTimelineLoading(false);
    }
  };

  const explainTimelineWithAI = async () => {
    try {
      setAiLoading(true);
      setAiError(null);
      setAiText("");

      const token =
        localStorage.getItem("customer_token") ||
        localStorage.getItem("token");

      const res = await fetch(`${API}/api/ai/explain-status`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          role: "customer",
          orderId: selectedOrderId,
          events: timeline,
        }),
      });

      if (!res.ok) {
        throw new Error(`AI explain failed (${res.status})`);
      }

      const data = await res.json();

      setAiText(data.text || data.message || JSON.stringify(data));
    } catch (e) {
      setAiError(e.message);
    } finally {
      setAiLoading(false);
    }
  };

   const getOrderId = (order) => {
    const raw =
      order.order_id ||
      order.id ||
      order.orderNumber ||
      order.order_number ||
      '';

    return String(raw).replace(/^ORD-/i, '').trim();
  };

  const hasUnreadNegotiation = (order) => {
  const orderId = getOrderId(order);

  return Object.values(unreadNegotiations).some(
    (item) => String(item?.orderId || "") === String(orderId)
  );
};

  const getOrderNumber = (order) => {
    const value =
      order.orderNumber ||
      order.order_number ||
      order.order_id ||
      order.id ||
      "—";

    if (value === "—") return value;

    return String(value).startsWith("ORD-") ? value : `ORD-${value}`;
  };

  const getOrderDate = (order) => {
    const rawDate = order.date || order.created_at || order.createdAt;

    if (!rawDate) return "—";

    const parsed = new Date(rawDate);

    if (Number.isNaN(parsed.getTime())) {
      return rawDate;
    }

    return parsed.toLocaleDateString();
  };

  const cleanValue = (value) => {
    if (value === null || value === undefined || value === "") {
      return "—";
    }

    return value;
  };

  const getLoadSummary = (order) => {
    return (
      order.load_description ||
      order.package_description ||
      order.description ||
      order.packageInfo ||
      order.package_info ||
      order.shipment_type ||
      "Load details not provided"
    );
  };

    const getRoute = (order) => {
    const pickup = order.from_address || "Pickup not provided";
    const dropoff = order.to_address || "Drop-off not provided";
    return `${pickup} → ${dropoff}`;
  };

  const getTruckType = (order) => {
    return (
      order.required_truck_type ||
      order.truck_type ||
      order.vehicle_type ||
      order.vehicleType ||
      order.truckBodyType ||
      order.truck_body_type ||
      "—"
    );
  };

  const getDistance = (order) => {
    const value =
      order.distance_in_miles ||
      order.distance_miles ||
      order.distance ||
      order.miles;

    if (value === null || value === undefined || value === "") return "—";

    return `${value} mi`;
  };

  const getWeight = (order) => {
    const value = order.weight || order.weight_lbs || order.load_weight;

    if (value === null || value === undefined || value === "") return "—";

    return `${value} lbs`;
  };

  const getTotal = (order) => {
    const value =
      order.total ||
      order.customer_price ||
      order.price ||
      order.amount ||
      order.total_amount;

    if (value === null || value === undefined || value === "") return "—";

    if (typeof value === "number") {
      return `$${value.toFixed(2)}`;
    }

    const stringValue = String(value);

    if (stringValue.startsWith("$")) {
      return stringValue;
    }

    const numberValue = Number(stringValue);

    if (!Number.isNaN(numberValue)) {
      return `$${numberValue.toFixed(2)}`;
    }

    return stringValue;
  };

  const getAiTip = (order) => {
    const value =
      order.ai_tip ||
      order.aiTip ||
      order.ai_price_tip ||
      order.ai_recommendation;

    if (!value || String(value).includes("packageInfo")) return "—";

    return value;
  };

  const isOrderOpenForPricing = (order) => {
    const status = (order.status || "").toString().toUpperCase();
    const isAssigned = order.courier_id != null;

    if (isAssigned) return false;

    if (!status) return true;

    return ["CREATED", "OPEN", "PLACED", "NEW"].includes(status);
  };

  const canPayOrder = (order) => {
    const status = (order.status || "").toString().toUpperCase();

    const paymentStatus = (order.payment_status || "")
      .toString()
      .toLowerCase();

    if (paymentStatus === "paid") return false;

    // Only allow payment when an agreed price exists.
    const agreedCents = Number(order.agreed_price_cents);
    if (!Number.isSafeInteger(agreedCents) || agreedCents <= 0) {
      return false;
    }

    if (["DELIVERED", "CANCELLED", "CANCELED", "REFUNDED"].includes(status)) {
      return false;
    }

    return [
      "CREATED",
      "OPEN",
      "PLACED",
      "NEW",
      "ACCEPTED",
      "IN_TRANSIT",
      "ON_HOLD",
    ].includes(status);
  };

  const handlePayOrder = (order) => {
    const id = getOrderId(order);
    history.push(`/customer/payment-management?orderId=${id}`);
  };

  const getPaymentStatusLabel = (order) => {
    const paymentStatus = (order.payment_status || "")
      .toString()
      .toLowerCase();

    const orderStatus = (order.status || "").toString().toUpperCase();

    if (paymentStatus === "paid") return "Paid";
    if (paymentStatus === "pending") return "Pending";
    if (paymentStatus === "failed") return "Failed";

    if (
      [
        "DELIVERED",
        "ACCEPTED",
        "IN_TRANSIT",
        "ON_HOLD",
        "CREATED",
        "OPEN",
        "PLACED",
        "NEW",
      ].includes(orderStatus)
    ) {
      return "Unpaid";
    }

    return "—";
  };

  const getStatusColor = (status) => {
    const normalized = (status || "").toString().toUpperCase();

    if (["DELIVERED", "COMPLETED"].includes(normalized)) return "success";

    if (["ACCEPTED", "ASSIGNED", "IN_TRANSIT", "ON_HOLD"].includes(normalized)) {
      return "info";
    }

    if (["CANCELLED", "CANCELED", "FAILED"].includes(normalized)) return "error";

    if (["CREATED", "OPEN", "PLACED", "NEW"].includes(normalized)) {
      return "warning";
    }

    return "default";
  };

 const handleSearch = (e) => {
  const term = e.target.value;
  const normalizedTerm = term.toLowerCase().replace("ord-", "").trim();

  setSearchTerm(term);

  const filtered = orders.filter((order) => {
    const searchableText = [
      getOrderNumber(order),
      String(getOrderNumber(order)).replace("ORD-", ""),
      getOrderDate(order),
      order.status,
      getTruckType(order),
      getDistance(order),
      getWeight(order),
      getAiTip(order),
      getTotal(order),
      getPaymentStatusLabel(order),
      getLoadSummary(order),
    ]
      .join(" ")
      .toLowerCase()
      .replace("ord-", "");

    return searchableText.includes(normalizedTerm);
  });

  setFilteredOrders(filtered);
  setCurrentPage(1);
};

  const resetSearch = () => {
    setSearchTerm("");
    setFilteredOrders(orders);
    setCurrentPage(1);
  };

  const handleReorder = (orderId) => {
    console.log("Reordering load:", orderId);
  };

  const handleViewTimeline = (order) => {
    const id = getOrderId(order);

    setSelectedOrderId(id);
    setAiText("");
    setAiError(null);

    fetchTimeline(id);
  };

  const handleOpenBids = (order) => {
  const id = getOrderId(order);
  const unreadKey = "hagglefreight_customer_unread_negotiations";

  try {
    const saved = JSON.parse(
      localStorage.getItem(unreadKey) || "{}"
    );

    const updated = Object.fromEntries(
      Object.entries(saved).filter(
        ([, item]) =>
          String(item?.orderId || "") !== String(id)
      )
    );

    localStorage.setItem(
      unreadKey,
      JSON.stringify(updated)
    );

    setUnreadNegotiations(updated);

    window.dispatchEvent(
      new CustomEvent("hagglefreight-customer-unread-changed")
    );
  } catch (error) {
    console.error("Failed to clear unread negotiation:", error);
  }

  history.push(`/customer/orders/${id}/bids`);
};

  const indexOfLastOrder = currentPage * ordersPerPage;
  const indexOfFirstOrder = indexOfLastOrder - ordersPerPage;

  const currentOrders = filteredOrders.slice(indexOfFirstOrder, indexOfLastOrder);

  const totalPages = Math.ceil(filteredOrders.length / ordersPerPage);

  const paginate = (pageNumber) => {
    setCurrentPage(pageNumber);
    setSelectedOrderId(null);
    setTimeline([]);
    setTimelineError(null);
  };

  return (
    <CustomerLayout>
      <Box sx={{ width: "100%", overflowX: "hidden" }}>
        <Box
          sx={{
            mb: { xs: 3, md: 4 },
            maxWidth: 760,
            mx: "auto",
            textAlign: {
              xs: "left",
              md: "center",
            },
          }}
        >
          <Typography
            variant="h4"
            fontWeight={800}
            gutterBottom
            sx={{
              fontSize: {
                xs: "2rem",
                md: "2.5rem",
              },
            }}
          >
            Load History
          </Typography>

          <Typography variant="body1" color="text.secondary">
            View posted loads, payment status, bids, and timeline updates.
          </Typography>
        </Box>

        <Paper
          elevation={0}
          sx={{
            p: { xs: 2, md: 2.5 },
            mb: 3,
            borderRadius: 3,
            maxWidth: 1100,
            mx: "auto",
            border: "1px solid #e2e8f0",
          }}
        >
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} md={9}>
              <TextField
                label="Search Load"
                variant="outlined"
                fullWidth
                value={searchTerm}
                onChange={handleSearch}
                placeholder="Search by load number, status, payment, truck type, date, or item"
              />
            </Grid>

            <Grid item xs={12} md={3}>
              <Button
                variant="contained"
                fullWidth
                onClick={resetSearch}
                sx={{
                  textTransform: "none",
                  fontWeight: 700,
                  borderRadius: 2,
                  minHeight: 54,
                }}
              >
                Reset Search
              </Button>
            </Grid>
          </Grid>
        </Paper>

        {isMobileOrTablet ? (
          <Stack spacing={2} sx={{ maxWidth: 900, mx: "auto" }}>
            {currentOrders.length === 0 ? (
              <Paper
                elevation={0}
                sx={{
                  p: 3,
                  borderRadius: 3,
                  border: "1px solid #e2e8f0",
                  textAlign: "center",
                }}
              >
                <Typography color="text.secondary">No loads found.</Typography>
              </Paper>
            ) : (
              currentOrders.map((order) => (
                <Paper
                  key={getOrderId(order)}
                  elevation={0}
                  sx={{
                    p: { xs: 2, sm: 2.5 },
                    borderRadius: 3,
                    border: "1px solid #e2e8f0",
                    textAlign: "left",
                    width: "100%",
                    boxSizing: "border-box",
                  }}
                >
                  <Stack spacing={1.75}>
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        gap: 1.5,
                        alignItems: "flex-start",
                      }}
                    >
                      <Box sx={{ minWidth: 0 }}>
                        <Typography
                          fontWeight={800}
                          sx={{
                            wordBreak: "break-word",
                            fontSize: { xs: "1rem", sm: "1.1rem" },
                          }}
                        >
                          Load #{getOrderNumber(order)}
                        </Typography>

                        <Typography variant="body2" color="text.secondary">
                          {getOrderDate(order)}
                        </Typography>
                      </Box>

                      <Chip
                        label={order.status || "Unknown"}
                        color={getStatusColor(order.status)}
                        size="small"
                        variant="outlined"
                        sx={{ flexShrink: 0 }}
                      />
                    </Box>

                    <Divider />

                    <Box>
                      <Typography variant="body2" color="text.secondary">
                        Load Details
                      </Typography>

                      <Typography fontWeight={600} sx={{ wordBreak: "break-word" }}>
                        {getLoadSummary(order)}
                      </Typography>
                    </Box>

                                        <Box>
                      <Typography variant="body2" color="text.secondary">
                        Pickup
                      </Typography>
                      <Typography fontWeight={600} sx={{ overflowWrap: "anywhere" }}>
                        {order.from_address || "—"}
                      </Typography>
                    </Box>

                    <Box>
                      <Typography variant="body2" color="text.secondary">
                        Drop-off
                      </Typography>
                      <Typography fontWeight={600} sx={{ overflowWrap: "anywhere" }}>
                        {order.to_address || "—"}
                      </Typography>
                    </Box>

                    <Box>
                      <Typography variant="body2" color="text.secondary">
                        Service Type
                      </Typography>
                      <Typography fontWeight={600}>
                        {order.delivery_type || "—"}
                      </Typography>
                    </Box>

                    <Grid container spacing={1.5}>
                      <Grid item xs={6} sm={4}>
                        <Typography variant="body2" color="text.secondary">
                          Truck Type
                        </Typography>

                        <Typography fontWeight={600}>
                          {getTruckType(order)}
                        </Typography>
                      </Grid>

                      <Grid item xs={6} sm={4}>
                        <Typography variant="body2" color="text.secondary">
                          Distance
                        </Typography>

                        <Typography fontWeight={600}>
                          {getDistance(order)}
                        </Typography>
                      </Grid>

                      <Grid item xs={6} sm={4}>
                        <Typography variant="body2" color="text.secondary">
                          Weight
                        </Typography>

                        <Typography fontWeight={600}>
                          {getWeight(order)}
                        </Typography>
                      </Grid>

                      <Grid item xs={6} sm={4}>
                        <Typography variant="body2" color="text.secondary">
                          AI Tip
                        </Typography>

                        <Typography fontWeight={600}>
                          {getAiTip(order)}
                        </Typography>
                      </Grid>

                      <Grid item xs={6} sm={4}>
                        <Typography variant="body2" color="text.secondary">
                          Total
                        </Typography>

                        <Typography fontWeight={600}>
                          {getTotal(order)}
                        </Typography>
                      </Grid>

                      <Grid item xs={6} sm={4}>
                        <Typography
                          variant="body2"
                          color="text.secondary"
                          sx={{ mb: 0.5 }}
                        >
                          Payment
                        </Typography>

                        <Chip
                          label={getPaymentStatusLabel(order)}
                          color={
                            getPaymentStatusLabel(order) === "Paid"
                              ? "success"
                              : "warning"
                          }
                          size="small"
                          variant="outlined"
                        />
                      </Grid>
                    </Grid>

                    <Divider />

                    <Stack
                      direction={{ xs: "column", sm: "row" }}
                      spacing={1}
                      sx={{ width: "100%" }}
                    >
                      <Button
                        variant="outlined"
                        fullWidth
                        onClick={() => handleViewTimeline(order)}
                        sx={{
                          textTransform: "none",
                          fontWeight: 700,
                          borderRadius: 2,
                        }}
                      >
                        Timeline
                      </Button>

                      <Button
                        variant="outlined"
                        fullWidth
                        onClick={() => handleOpenBids(order)}
                        sx={{
                           textTransform: "none",
                           fontWeight: 700,
                           borderRadius: 2,
                        }}
                       >
                         {hasUnreadNegotiation(order) ? "Bids / Chat • NEW" : "Bids / Chat"}
                       </Button>

                      <Button
                        variant="contained"
                        fullWidth
                        onClick={() => handlePayOrder(order)}
                        disabled={!canPayOrder(order)}
                        sx={{
                          textTransform: "none",
                          fontWeight: 700,
                          borderRadius: 2,
                        }}
                      >
                        Pay
                      </Button>

                      <Button
                        variant="text"
                        fullWidth
                        onClick={() => handleReorder(getOrderNumber(order))}
                        sx={{
                          textTransform: "none",
                          fontWeight: 700,
                          borderRadius: 2,
                        }}
                      >
                        Reorder
                      </Button>
                    </Stack>
                  </Stack>
                </Paper>
              ))
            )}
          </Stack>
        ) : (
          <TableContainer
            component={Paper}
            elevation={0}
            sx={{
              borderRadius: 3,
              overflowX: "auto",
              overflowY: "hidden",
              maxWidth: 1200,
              mx: "auto",
              border: "1px solid #e2e8f0",
              WebkitOverflowScrolling: "touch",
              "&::-webkit-scrollbar": {
                height: 10,
              },
              "&::-webkit-scrollbar-thumb": {
                backgroundColor: "#cbd5e1",
                borderRadius: 10,
              },
              "&::-webkit-scrollbar-track": {
                backgroundColor: "#f8fafc",
              },
            }}
          >
            <Table
              stickyHeader
              sx={{
                minWidth: 1150,
                tableLayout: "fixed",
              }}
            >
              <TableHead>
                <TableRow>
                  <TableCell sx={{ width: 130, whiteSpace: "nowrap" }}>
                    Load Number
                  </TableCell>
                  <TableCell sx={{ width: 110, whiteSpace: "nowrap" }}>
                    Date
                  </TableCell>
                  <TableCell sx={{ width: 125, whiteSpace: "nowrap" }}>
                    Status
                  </TableCell>
                  <TableCell sx={{ width: 140, whiteSpace: "nowrap" }}>
                    Truck Type
                  </TableCell>
                  <TableCell sx={{ width: 110, whiteSpace: "nowrap" }}>
                    Distance
                  </TableCell>
                  <TableCell sx={{ width: 110, whiteSpace: "nowrap" }}>
                    Weight
                  </TableCell>
                  <TableCell sx={{ width: 150, whiteSpace: "nowrap" }}>
                    AI Tip
                  </TableCell>
                  <TableCell sx={{ width: 110, whiteSpace: "nowrap" }}>
                    Total
                  </TableCell>
                  <TableCell sx={{ width: 120, whiteSpace: "nowrap" }}>
                    Payment
                  </TableCell>
                  <TableCell
                    align="right"
                    sx={{ width: 220, whiteSpace: "nowrap" }}
                  >
                    Actions
                  </TableCell>
                </TableRow>
              </TableHead>

              <TableBody>
                {currentOrders.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={10} align="center">
                      <Typography color="text.secondary" sx={{ py: 3 }}>
                        No loads found.
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  currentOrders.map((order) => (
                    <TableRow key={getOrderId(order)} hover>
                      <TableCell sx={{ whiteSpace: "nowrap", fontWeight: 700 }}>
                        {getOrderNumber(order)}
                      </TableCell>

                      <TableCell sx={{ whiteSpace: "nowrap" }}>
                        {getOrderDate(order)}
                      </TableCell>

                      <TableCell sx={{ whiteSpace: "nowrap" }}>
                        <Chip
                          label={order.status || "Unknown"}
                          color={getStatusColor(order.status)}
                          size="small"
                          variant="outlined"
                        />
                      </TableCell>

                      <TableCell sx={{ whiteSpace: "nowrap" }}>
                        {getTruckType(order)}
                      </TableCell>

                      <TableCell sx={{ whiteSpace: "nowrap" }}>
                        {getDistance(order)}
                      </TableCell>

                      <TableCell sx={{ whiteSpace: "nowrap" }}>
                        {getWeight(order)}
                      </TableCell>

                      <TableCell
                        sx={{
                          whiteSpace: "nowrap",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                        }}
                        title={String(getAiTip(order))}
                      >
                        {getAiTip(order)}
                      </TableCell>

                      <TableCell sx={{ whiteSpace: "nowrap" }}>
                        {getTotal(order)}
                      </TableCell>

                      <TableCell sx={{ whiteSpace: "nowrap" }}>
                        <Chip
                          label={getPaymentStatusLabel(order)}
                          color={
                            getPaymentStatusLabel(order) === "Paid"
                              ? "success"
                              : "warning"
                          }
                          size="small"
                          variant="outlined"
                        />
                      </TableCell>

                      <TableCell align="right">
                        <Stack
                          direction="row"
                          spacing={1}
                          justifyContent="flex-end"
                          flexWrap="nowrap"
                        >
                          <Button
                            variant="outlined"
                            size="small"
                            onClick={() => handleViewTimeline(order)}
                            sx={{
                              textTransform: "none",
                              fontWeight: 700,
                              minWidth: 76,
                            }}
                          >
                            Timeline
                          </Button>

                          <Button
                            variant="outlined"
                            size="small"
                            onClick={() => handleOpenBids(order)}
                            sx={{
                             textTransform: "none",
                             fontWeight: 700,
                             minWidth: 58,
                            }}
                          >
                            {hasUnreadNegotiation(order) ? "Bids / Chat • NEW" : "Bids / Chat"}
                          </Button>

                          <Button
                            variant="contained"
                            color="primary"
                            size="small"
                            onClick={() => handlePayOrder(order)}
                            disabled={!canPayOrder(order)}
                            sx={{
                              textTransform: "none",
                              fontWeight: 700,
                              minWidth: 54,
                            }}
                          >
                            Pay
                          </Button>

                          <Button
                            variant="text"
                            size="small"
                            onClick={() => handleReorder(getOrderNumber(order))}
                            sx={{
                              textTransform: "none",
                              fontWeight: 700,
                              minWidth: 70,
                            }}
                          >
                            Reorder
                          </Button>
                        </Stack>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        )}

        {selectedOrderId && (
          <Paper
            elevation={0}
            sx={{
              mt: 3,
              p: { xs: 2.25, md: 3 },
              borderRadius: 3,
              textAlign: "left",
              maxWidth: 900,
              mx: "auto",
              border: "1px solid #e2e8f0",
            }}
          >
            <Typography variant="h6" fontWeight={800} gutterBottom>
              Load Timeline #{selectedOrderId}
            </Typography>

            {timelineLoading && <Typography>Loading timeline...</Typography>}

            {timelineError && (
              <Typography color="error">{timelineError}</Typography>
            )}

            {!timelineLoading && !timelineError && (
              <Box sx={{ mb: 2 }}>
                <Button
                  variant="contained"
                  size="small"
                  onClick={explainTimelineWithAI}
                  disabled={aiLoading || !timeline?.length}
                  sx={{
                    textTransform: "none",
                    fontWeight: 700,
                    borderRadius: 2,
                  }}
                >
                  {aiLoading ? "Explaining..." : "Explain with AI"}
                </Button>

                {aiError && (
                  <Typography color="error" sx={{ mt: 1 }}>
                    {aiError}
                  </Typography>
                )}

                {aiText && (
                  <Box
                    sx={{
                      mt: 2,
                      p: 2,
                      border: "1px solid #e2e8f0",
                      borderRadius: 2,
                      backgroundColor: "#f8fafc",
                    }}
                  >
                    <Typography fontWeight={800}>AI explanation:</Typography>

                    <Typography sx={{ mt: 0.75 }}>{aiText}</Typography>
                  </Box>
                )}
              </Box>
            )}

            {!timelineLoading && !timelineError && (
              <OrderTimeline events={timeline} />
            )}
          </Paper>
        )}

        {totalPages > 1 && (
          <Grid container spacing={1} justifyContent="center" sx={{ mt: 3 }}>
            {Array.from({ length: totalPages }, (_, index) => (
              <Grid item key={index}>
                <Button
                  variant={currentPage === index + 1 ? "contained" : "outlined"}
                  color="primary"
                  onClick={() => paginate(index + 1)}
                  sx={{
                    minWidth: 42,
                    borderRadius: 2,
                  }}
                >
                  {index + 1}
                </Button>
              </Grid>
            ))}
          </Grid>
        )}
      </Box>
    </CustomerLayout>
  );
};

export default OrderHistory;